import { useCallback, useMemo } from "react";
import LocalStorageHelper from "../helpers/LocalStorageHelper";

export default function useUrlFilters<F extends object>(
	emptyFilters: F,
	urlParams: URLSearchParams,
	setUrlParams: (newUrlParams: URLSearchParams) => void,
	urlParamsToFilters: (urlParams: URLSearchParams, emptyFilters: F) => F,
	filtersToUrlParams: (filters: F) => Record<string, string>,
	storageKey: string
): [F, (newFilters: F) => void] {
	const [storageFilters, setStorageFilters] =
		LocalStorageHelper.useLocalStorageState<F>(storageKey);

	const filters = useMemo<F>(
		() => urlParamsToFilters(urlParams, { ...emptyFilters, ...storageFilters }),
		[urlParams, storageFilters]
	);

	const setFilters = useCallback(
		(newFilters: F) => {
			const filterParams = filtersToUrlParams(newFilters);
			const unchangedParams = Object.fromEntries(
				Object.entries(Object.fromEntries(urlParams.entries())).filter(
					([k]) => !(k in newFilters)
				)
			);
			setUrlParams(
				new URLSearchParams({
					...unchangedParams,
					...filterParams
				})
			);

			setStorageFilters({ ...unchangedParams, ...newFilters });
		},
		[urlParams, setUrlParams, setStorageFilters]
	);

	return [filters, setFilters];
}
